import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Field, Input, Modal } from '../../components/Ui';
import { useToast } from '../../contexts/ToastContext';
import { useAdjustInventoryStock } from '../../hooks/businessData';
import type { BranchInventoryRow } from '../../../shared/types';

interface InventoryQuickAdjustModalProps {
  branchId: string;
  productId: string;
  productName: string;
  row?: BranchInventoryRow;
  open: boolean;
  onOpenChange(open: boolean): void;
}

export function InventoryQuickAdjustModal({ branchId, productId, productName, row, open, onOpenChange }: InventoryQuickAdjustModalProps) {
  const { show } = useToast();
  const adjustStock = useAdjustInventoryStock();
  const [operation, setOperation] = useState<'add' | 'subtract'>('add');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setOperation('add');
    setQuantity('1');
    setNote('');
    setError('');
  }, [open]);

  const context = useMemo(() => {
    if (!row) return null;
    const baseLabel = row.mode === 'template' ? 'Plantilla hoy' : 'Base actual';
    const available = row.effectiveStock ?? Math.max(0, row.stockQuantity - row.consumedToday);
    return { baseLabel, base: row.stockQuantity, consumed: row.consumedToday, available };
  }, [row]);

  const setQuickAmount = (amount: number) => {
    setOperation('add');
    setQuantity(String(amount));
    setError('');
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const amount = Number(quantity);
    if (!Number.isInteger(amount) || amount <= 0) {
      setError('La cantidad debe ser un entero mayor a cero.');
      return;
    }
    const delta = operation === 'add' ? amount : -amount;
    try {
      await adjustStock.mutateAsync({ branchId, productId, delta, note });
      show('Ajuste rápido aplicado.', 'success');
      onOpenChange(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'No se pudo ajustar el stock.');
    }
  };

  return (
    <Modal open={open} onClose={() => onOpenChange(false)} title={`Ajuste rápido · ${productName}`}>
      <form className="grid gap-4" onSubmit={submit}>
        {context ? (
          <div className="flex flex-wrap gap-2 rounded-2xl border border-[var(--app-brand-line)] bg-[var(--app-brand-soft)] p-3 text-sm">
            <Badge tone="blue">{context.baseLabel}: {context.base}</Badge>
            <Badge tone="amber">Consumido hoy: {context.consumed}</Badge>
            <Badge tone={context.available <= 0 ? 'red' : 'green'}>Disponible: {context.available}</Badge>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant={operation === 'add' ? 'primary' : 'secondary'} onPress={() => setOperation('add')}>Sumar</Button>
          <Button type="button" variant={operation === 'subtract' ? 'primary' : 'secondary'} onPress={() => setOperation('subtract')}>Restar</Button>
          {[1, 5, 10].map((amount) => (
            <Button key={amount} type="button" variant="ghost" onPress={() => setQuickAmount(amount)}>+{amount}</Button>
          ))}
        </div>

        <Field label="Cantidad" hint="Ingresá una cantidad positiva; el signo lo define Sumar o Restar.">
          <Input type="number" min={1} step={1} value={quantity} onChange={(event) => { setQuantity(event.target.value); setError(''); }} />
        </Field>
        <Field label="Nota opcional">
          <Input value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} placeholder="Motivo del ajuste" />
        </Field>
        {error ? <span className="text-sm font-bold text-red-600">{error}</span> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onPress={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" busy={adjustStock.isPending}>Aplicar ajuste</Button>
        </div>
      </form>
    </Modal>
  );
}
